// The Docs Hub edit lease (design §5.1): one editor at a time, held in
// Postgres, always expiring. Entering edit mode acquires it, a heartbeat renews
// it by re-posting the same token, and save/cancel/unmount release it. Every
// other path (crashed tab, killed browser) is covered by the TTL, which is why
// there is no `sendBeacon` fallback — a beacon cannot carry the identity Bearer
// header, so it would fail auth and buy nothing the TTL does not already give.
//
// The proxy calls are injected rather than called through RTK hooks here, so the
// lifecycle (acquire once, renew on cadence, release on teardown) is testable
// without a store.

import { useCallback, useEffect, useRef, useState } from 'react'
import type {
  AcquireDocLockInput,
  AcquireDocLockResponse
} from '@axilon-apps/domains-ontology-ui-state'

import { readErrorMessage, readLockConflict, type LockConflict } from './doc-helpers'

/** Fallback renew cadence when the proxy does not advertise one. */
export const DEFAULT_LEASE_HEARTBEAT_MS = 30_000

export interface EditLeaseTransport {
  acquire: (input: AcquireDocLockInput) => Promise<AcquireDocLockResponse>
  release: (lockToken: string) => Promise<unknown>
}

export type EditLeaseStatus = 'idle' | 'acquiring' | 'held' | 'blocked'

export interface EditLease {
  status: EditLeaseStatus
  /** The live lock token, required on every guarded write. */
  token: string | null
  /**
   * The `current_revision_id` returned by the most recent successful acquire.
   * Use this — not the (possibly stale) list-cache value — for `expected_revision_id`
   * on saves so the guard carries the revision that was current when the lease started.
   */
  revisionId: string | null
  /** Who holds the lease when we could not take it. */
  blockedBy: LockConflict | null
  /** Non-conflict failure message (network, 500, …). */
  error: string | null
  /** Re-acquire — the takeover path once a foreign lease has expired. */
  retry: () => void
  /** Release now; safe when nothing is held. */
  release: () => Promise<void>
}

export interface UseEditLeaseArgs {
  docId: string | null
  /** Acquire while true; release as soon as it goes false. */
  active: boolean
  /** `lock_holder_name` — the signed-in user. */
  holderName: string
  transport: EditLeaseTransport
  /** Test seam / override for the renew cadence. */
  heartbeatMs?: number
}

export function useEditLease({
  docId,
  active,
  holderName,
  transport,
  heartbeatMs
}: UseEditLeaseArgs): EditLease {
  const transportRef = useRef(transport)
  transportRef.current = transport
  const holderRef = useRef(holderName)
  holderRef.current = holderName
  const tokenRef = useRef<string | null>(null)
  // The last token we were issued for this doc, kept across re-acquires.
  // Releasing is fire-and-forget, so a retry can outrun its own release and
  // find the lock still held — by us. Presenting the token makes that acquire a
  // renew instead of a collision, while a lock some *other* editor took is
  // still refused (the proxy matches on the caller's token).
  const lastTokenRef = useRef<string | null>(null)

  const [token, setToken] = useState<string | null>(null)
  const [revisionId, setRevisionId] = useState<string | null>(null)
  const [status, setStatus] = useState<EditLeaseStatus>('idle')
  const [blockedBy, setBlockedBy] = useState<LockConflict | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)

  const releaseToken = useCallback(async (held: string | null): Promise<void> => {
    if (held === null) return
    try {
      await transportRef.current.release(held)
    } catch {
      // A failed release is not worth surfacing: the lease expires on its own.
    }
  }, [])

  const release = useCallback(async (): Promise<void> => {
    const held = tokenRef.current
    tokenRef.current = null
    lastTokenRef.current = null
    setToken(null)
    setRevisionId(null)
    setStatus('idle')
    await releaseToken(held)
  }, [releaseToken])

  const retry = useCallback((): void => {
    setAttempt((previous) => previous + 1)
  }, [])

  // A token only means anything for the doc it was issued for. Declared before
  // the lease effect so it runs first when the open doc changes.
  useEffect(() => {
    lastTokenRef.current = null
  }, [docId])

  useEffect(() => {
    if (!active || docId === null) return
    let cancelled = false
    let timer: ReturnType<typeof setInterval> | undefined

    setStatus('acquiring')
    setBlockedBy(null)
    setError(null)

    const renew = (period: number): void => {
      timer = setInterval(() => {
        const current = tokenRef.current
        if (current === null) return
        transportRef.current
          .acquire({ holder_kind: 'user', holder_name: holderRef.current, lock_token: current })
          .catch((err: unknown) => {
            if (cancelled) return
            // Losing the lease mid-edit (expired, then taken over) must show up
            // before the user hits save and eats a 409.
            tokenRef.current = null
            setToken(null)
            setStatus('blocked')
            setBlockedBy(readLockConflict(err))
            setError(
              readLockConflict(err) === null ? readErrorMessage(err, 'Lost the edit lock') : null
            )
            if (timer) clearInterval(timer)
          })
      }, period)
    }

    void (async () => {
      try {
        const held = lastTokenRef.current
        const lease = await transportRef.current.acquire({
          holder_kind: 'user',
          holder_name: holderRef.current,
          ...(held === null ? {} : { lock_token: held })
        })
        if (cancelled) {
          void transportRef.current.release(lease.lock_token).catch(() => undefined)
          return
        }
        tokenRef.current = lease.lock_token
        lastTokenRef.current = lease.lock_token
        setToken(lease.lock_token)
        setRevisionId(lease.current_revision_id ?? null)
        setStatus('held')
        renew(
          heartbeatMs ??
            (lease.heartbeat_seconds > 0
              ? lease.heartbeat_seconds * 1000
              : DEFAULT_LEASE_HEARTBEAT_MS)
        )
      } catch (err: unknown) {
        if (cancelled) return
        const conflict = readLockConflict(err)
        setStatus('blocked')
        setBlockedBy(conflict)
        setError(conflict === null ? readErrorMessage(err, 'Could not start editing') : null)
      }
    })()

    return () => {
      cancelled = true
      if (timer) clearInterval(timer)
      // `lastTokenRef` deliberately survives: a retry presents it so its own
      // still-in-flight release cannot look like someone else's lock.
      const held = tokenRef.current
      tokenRef.current = null
      setToken(null)
      setRevisionId(null)
      setStatus('idle')
      void releaseToken(held)
    }
  }, [docId, active, attempt, heartbeatMs, releaseToken])

  return { status, token, revisionId, blockedBy, error, retry, release }
}
